import { useCallback } from 'react';

import { useNode } from '@/components/diagrams/FlowChart/hooks/useNode';
import {
  TileEditModal,
  TileEditModalFormShape,
  TileEditModalProps,
} from '@/components/diagrams/FlowChart/nodes/TileNode/TileEditModal';
import { TileNode } from '@/components/diagrams/FlowChart/types';

import { useEstateWaterfallContext } from '../contexts/estateWaterfall.context';

/**
 * Shows the shared `TileEditModal` and saves through the estate waterfall
 * reducer.
 *
 * NOTE: The tile settings modals of the diagram editors are different:
 * - This modal edits "Display as", the notes (with "Generate"), and "Show
 *   itemized tax values". It does not edit the tile text, because the text
 *   comes from household data: the name, the kind, and the values.
 * - The entity diagram uses the same shared modal, without the tax setting.
 * - The presentation diagram uses a copy of the form that also edits the name
 *   and the type (`PresentationDiagramTileEditModal`).
 * - The artifact diagram builder edits only the three text lines
 *   (`TileTextEditModal`).
 * Moving all editors to one modal needs design input: https://linear.app/luminary/issue/T1-7153
 */
export function EstateWaterfallTileEditModal(props: TileEditModalProps) {
  const { dispatch } = useEstateWaterfallContext();
  const node = useNode<TileNode>();

  // Handle saves via the reducer to maintain single source of truth.
  // The reducer already has the "previous" state in state.nodes when the action runs,
  // so we just pass the new values and let it handle snapshotting.
  const onSaveData = useCallback(
    (formData: TileEditModalFormShape) => {
      dispatch({
        type: 'UPDATE_TILE_CONFIG',
        nodeId: node.id,
        notes: formData.notes,
        componentType: formData.componentType,
        iconShape: formData.iconShape,
        estateWaterfallConfig: formData.estateWaterfallConfig,
      });
    },
    [dispatch, node.id]
  );

  return <TileEditModal {...props} onSaveData={onSaveData} />;
}
